import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket, EventTypes } from '../context/WebSocketContext';
import { 
  getChargerDetailed,
  getChargerEnergy,
  getChargerKpis,
  getChargerSessions,
  getChargerErrors,
  getChargerConnectors,
  getChargerDiagnostics,
  sendChargerRestart,
  sendChargerReset,
  sendChargerAvailability,
  clearChargerCache,
  resetChargerPlatformState,
  generateChargerQR,
  deleteCharger,
  createConnector,
  uploadChargerFirmware,
  forceChargerCommand,
  forceReconnect,
  remoteStartTransaction,
  getClientsSimple
} from '../lib/api';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Zap, MoreHorizontal, Trash2, Wifi, WifiOff, 
  RefreshCw, Power, PowerOff, QrCode, RotateCcw,
  ArrowLeft, MapPin, Clock, Activity, AlertTriangle,
  BarChart3, History, AlertCircle, Plug, Plus,
  Battery, TrendingUp, Calendar, User, Info, Settings, Terminal,
  RefreshCcw, Play, StopCircle, Pencil, Hammer, Unlock, Database,
  Copy, Link, CheckCircle, ExternalLink, Edit, Search, FileText, Eye, Upload, FileSpreadsheet
} from 'lucide-react';
import AppChargesExcelDialog from '../components/reports/AppChargesExcelDialog';
import { toast } from 'sonner';
import { formatCurrency, formatDate, formatDuration, formatEnergy, cn } from '../lib/utils';
import { QRCodeSVG } from 'qrcode.react';
import { SessionDetailsModal, InvoicePopup } from '../components/sessions';
import { generateInvoiceNumber, normalizeChargingInvoice } from '../components/sessions/ChargingInvoiceDocument';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const SESSION_STATUS_OPTIONS = [
  { value: 'all', label: 'Të gjitha' },
  { value: 'Charging', label: 'Duke karikuar' },
  { value: 'Preparing', label: 'Në përgatitje' },
  { value: 'Completed', label: 'Të përfunduara' },
  { value: 'Stopped', label: 'Të ndalura' },
  { value: 'Error', label: 'Gabim' },
];

const getInvoiceNumber = (session) => generateInvoiceNumber(normalizeChargingInvoice(session));

const isBillableSession = (session) => {
  const status = String(session?.status || '').toLowerCase();
  return Boolean(
    session?.invoice_number ||
    session?.invoice_id ||
    Number(session?.cost_lek || 0) > 0 ||
    status === 'completed' ||
    status === 'stopped'
  );
};

const ChargerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isModerator = user?.role === 'MODERATOR';
  const canManageCharger = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const { subscribe, connected: wsConnected, isCommandPending } = useWebSocket();
  
  const [charger, setCharger] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Energy data
  const [energyData, setEnergyData] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [energyGroupBy, setEnergyGroupBy] = useState('day');
  const [energyLoading, setEnergyLoading] = useState(false);
  
  // Sessions data
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionStatusFilter, setSessionStatusFilter] = useState('all');
  const [sessionUserFilter, setSessionUserFilter] = useState('all');
  const [sessionSearchQuery, setSessionSearchQuery] = useState('');
  const [detailsSession, setDetailsSession] = useState(null);
  const [invoicePreviewSession, setInvoicePreviewSession] = useState(null);
  const [excelOpen, setExcelOpen] = useState(false);

  // Charger invoices
  const [invoiceSessions, setInvoiceSessions] = useState([]);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  
  // Errors data
  const [errors, setErrors] = useState([]);
  const [errorsTotal, setErrorsTotal] = useState(0);
  const [errorsPage, setErrorsPage] = useState(1);
  const [errorsLoading, setErrorsLoading] = useState(false);
  
  // Diagnostics data
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  
  // Date range
  const [dateRange, setDateRange] = useState('7d');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  
  // Dialogs
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectorDialogOpen, setConnectorDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [firmwareDialogOpen, setFirmwareDialogOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [availabilityReason, setAvailabilityReason] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [firmwareVersion, setFirmwareVersion] = useState('');
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [firmwareLoading, setFirmwareLoading] = useState(false);
  
  // Remote Start Transaction Dialog
  const [remoteStartDialogOpen, setRemoteStartDialogOpen] = useState(false);
  const [remoteStartConnector, setRemoteStartConnector] = useState(null);
  const [remoteStartUserId, setRemoteStartUserId] = useState('');
  const [remoteStartLoading, setRemoteStartLoading] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  
  // Command loading states
  const [restartLoading, setRestartLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  
  // Connector types from server
  const [connectorTypes, setConnectorTypes] = useState([
    { code: 'Type2', name: 'Type 2' },
    { code: 'CCS', name: 'CCS' },
    { code: 'CHAdeMO', name: 'CHAdeMO' },
  ]);
  
  const [connectorForm, setConnectorForm] = useState({
    connector_id: '',
    connector_type: 'Type2',
    max_power_kw: ''
  });

  // Fetch connector types
  useEffect(() => {
    const fetchConnectorTypes = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connector-types/active`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.connector_types?.length > 0) {
            setConnectorTypes(data.connector_types);
          }
        }
      } catch (error) {
        console.error('Error fetching connector types:', error);
      }
    };
    fetchConnectorTypes();
  }, [token]);

  // Subscribe to WebSocket events for this charger
  useEffect(() => {
    if (!id) return;

    // Handle status changes for this charger
    const matchesCharger = (chargerId) => chargerId === id;
    const handleStatusChange = (data) => {
      setCharger(prev => {
        if (!prev) return prev;
        if (prev.id !== data.charger_id && prev.charger_id !== data.charger_id) return prev;
        return {
          ...prev,
          status: data.new_status,
          is_online: data.is_online !== undefined ? data.is_online : prev.is_online
        };
      });
    };

    // Handle charger online event
    const handleOnline = (data) => {
      setCharger(prev => {
        if (!prev) return prev;
        if (prev.id !== data.charger_id && prev.charger_id !== data.charger_id) return prev;
        return {
          ...prev,
          is_online: true,
          status: prev.status === 'Offline' ? 'Available' : prev.status
        };
      });
    };

    // Handle charger offline event
    const handleOffline = (data) => {
      setCharger(prev => {
        if (!prev) return prev;
        if (prev.id !== data.charger_id && prev.charger_id !== data.charger_id) return prev;
        return {
          ...prev,
          is_online: false,
          status: 'Offline'
        };
      });
    };

    // Handle command accepted - stop loading spinners
    const handleCommandAccepted = (data) => {
      if (data.charger_id === id) {
        setRestartLoading(false);
        setResetLoading(false);
        setActivateLoading(false);
        setDeactivateLoading(false);
      }
    };

    // Handle command rejected - stop loading spinners
    const handleCommandRejected = (data) => {
      if (data.charger_id === id) {
        setRestartLoading(false);
        setResetLoading(false);
        setActivateLoading(false);
        setDeactivateLoading(false);
      }
    };

    // Subscribe to events
    const unsubStatus = subscribe(EventTypes.CHARGER_STATUS_CHANGED, handleStatusChange);
    const unsubAvail = subscribe(EventTypes.CHARGER_AVAILABILITY_CHANGED, handleStatusChange);
    const unsubOnline = subscribe(EventTypes.CHARGER_CONNECTION_ONLINE, handleOnline);
    const unsubOffline = subscribe(EventTypes.CHARGER_CONNECTION_OFFLINE, handleOffline);
    const unsubAccepted = subscribe(EventTypes.COMMAND_ACCEPTED, handleCommandAccepted);
    const unsubRejected = subscribe(EventTypes.COMMAND_REJECTED, handleCommandRejected);
    const unsubTimeout = subscribe(EventTypes.COMMAND_TIMEOUT, handleCommandRejected);

    // Subscribe to real-time connector status updates
    const unsubConnector = subscribe('connector.status.updated', (data) => {
      // Match by charger_id (OCPP ID) or id
      setCharger(prev => {
        if (!prev) return prev;
        if (prev.id !== data.charger_id && prev.charger_id !== data.charger_id) return prev;
        return {
          ...prev,
          status: data.charger_status || prev.status,
        };
      });
      // Update the separate connectors state (used for rendering)
      setConnectors(prev => prev.map(conn => {
        if (conn.connector_id === data.connector_id) {
          return {
            ...conn,
            status: data.status,
            current_power_kw: data.current_power_kw ?? conn.current_power_kw ?? 0,
            soc_percent: data.soc_percent ?? conn.soc_percent ?? null
          };
        }
        return conn;
      }));
    });

    // Subscribe to session progress for live power/SoC updates
    const unsubProgress = subscribe('session.progress', (data) => {
      setConnectors(prev => prev.map(conn => {
        if (conn.connector_id === data.connector_id) {
          return {
            ...conn,
            current_power_kw: data.power_kw ?? data.current_power_kw ?? conn.current_power_kw,
            soc_percent: data.soc_percent ?? conn.soc_percent
          };
        }
        return conn;
      }));
    });

    // Cleanup subscriptions
    return () => {
      unsubStatus();
      unsubAvail();
      unsubOnline();
      unsubOffline();
      unsubAccepted();
      unsubRejected();
      unsubTimeout();
      unsubConnector();
      unsubProgress();
    };
  }, [id, subscribe]);

  const getDateRange = useCallback(() => {
    const now = new Date();
    let fromDate = new Date();
    
    switch (dateRange) {
      case '24h':
        fromDate.setDate(now.getDate() - 1);
        break;
      case '7d':
        fromDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        fromDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        fromDate.setDate(now.getDate() - 90);
        break;
      case 'custom':
        if (customDateFrom && customDateTo) {
          return {
            from: new Date(customDateFrom).toISOString(),
            to: new Date(customDateTo + 'T23:59:59').toISOString()
          };
        }
        // Fallback to 7 days if custom dates not set
        fromDate.setDate(now.getDate() - 7);
        break;
      default:
        fromDate.setDate(now.getDate() - 7);
    }
    
    return {
      from: fromDate.toISOString(),
      to: now.toISOString()
    };
  }, [dateRange, customDateFrom, customDateTo]);

  const fetchCharger = useCallback(async () => {
    try {
      setLoading(true);
      const [chargerRes, connectorsRes] = await Promise.all([
        getChargerDetailed(id),
        getChargerConnectors(id)
      ]);
      setCharger(chargerRes.data);
      setConnectors(connectorsRes.data);
    } catch (error) {
      console.error('Failed to fetch charger:', error);
      toast.error('Gabim në ngarkimin e të dhënave');
      if (error.response?.status === 404) {
        navigate('/chargers');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchEnergy = useCallback(async () => {
    try {
      setEnergyLoading(true);
      const { from, to } = getDateRange();
      const [energyRes, kpisRes] = await Promise.all([
        getChargerEnergy(id, from, to, energyGroupBy),
        getChargerKpis(id, from, to)
      ]);
      setEnergyData(energyRes.data.data || []);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Failed to fetch energy data:', error);
    } finally {
      setEnergyLoading(false);
    }
  }, [energyGroupBy, getDateRange, id]);

  const fetchSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const { from, to } = getDateRange();
      const res = await getChargerSessions(id, {
        from_date: from,
        to_date: to,
        status: sessionStatusFilter !== 'all' ? sessionStatusFilter : undefined,
        user_id: sessionUserFilter !== 'all' ? sessionUserFilter : undefined,
        search: sessionSearchQuery || undefined,
        page: sessionsPage,
        limit: 10
      });
      setSessions(res.data.sessions || []);
      setSessionsTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  }, [id, getDateRange, sessionSearchQuery, sessionStatusFilter, sessionUserFilter, sessionsPage]);

  const fetchInvoices = useCallback(async () => {
    try {
      setInvoicesLoading(true);
      const { from, to } = getDateRange();
      const res = await getChargerSessions(id, {
        from_date: from,
        to_date: to,
        status: sessionStatusFilter !== 'all' ? sessionStatusFilter : undefined,
        user_id: sessionUserFilter !== 'all' ? sessionUserFilter : undefined,
        search: sessionSearchQuery || undefined,
        page: invoicePage,
        limit: 10
      });
      const items = res.data.sessions || [];
      setInvoiceSessions(items);
      setInvoiceTotal(res.data.total || items.length);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  }, [id, getDateRange, invoicePage, sessionSearchQuery, sessionStatusFilter, sessionUserFilter]);

  const fetchErrors = useCallback(async () => {
    try {
      setErrorsLoading(true);
      const { from, to } = getDateRange();
      const res = await getChargerErrors(id, {
        from_date: from,
        to_date: to,
        page: errorsPage,
        limit: 10
      });
      setErrors(res.data.errors || []);
      setErrorsTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch errors:', error);
    } finally {
      setErrorsLoading(false);
    }
  }, [errorsPage, getDateRange, id]);

  const fetchDiagnostics = useCallback(async () => {
    try {
      setDiagnosticsLoading(true);
      const res = await getChargerDiagnostics(id);
      setDiagnostics(res.data);
    } catch (error) {
      console.error('Failed to fetch diagnostics:', error);
    } finally {
      setDiagnosticsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCharger();
  }, [fetchCharger]);

  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'energy') {
      fetchEnergy();
      fetchSessions();
    } else if (activeTab === 'sessions') {
      fetchSessions();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'errors') {
      fetchErrors();
    } else if (activeTab === 'diagnostics') {
      fetchDiagnostics();
    }
  }, [activeTab, fetchDiagnostics, fetchEnergy, fetchErrors, fetchInvoices, fetchSessions]);

  useEffect(() => {
    setSessionsPage(1);
    setInvoicePage(1);
  }, [dateRange, customDateFrom, customDateTo, sessionSearchQuery, sessionStatusFilter, sessionUserFilter]);

  // Actions - Remote Commands
  const handleRestart = async () => {
    setRestartLoading(true);
    try {
      await sendChargerRestart(id);
      toast.success('Komanda Rindiz (Soft Reset) u dërgua');
      fetchCharger();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Gabim në dërgimin e komandës';
      toast.error(msg);
    } finally {
      setRestartLoading(false);
    }
  };

  const handleReset = async () => {
    setResetLoading(true);
    try {
      await sendChargerReset(id, 'Hard', resetReason);
      toast.success('Komanda Reset (Hard Reset) u dërgua');
      setResetDialogOpen(false);
      setResetReason('');
      fetchCharger();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Gabim në dërgimin e komandës';
      toast.error(msg);
    } finally {
      setResetLoading(false);
    }
  };

  const handleActivate = async () => {
    setActivateLoading(true);
    try {
      await sendChargerAvailability(id, true, '');
      toast.success('Karikuesi u aktivizua (Available)');
      fetchCharger();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Gabim';
      toast.error(msg);
    } finally {
      setActivateLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await sendChargerAvailability(id, false, availabilityReason);
      toast.success('Karikuesi u çaktivizua (Unavailable)');
      setAvailabilityDialogOpen(false);
      setAvailabilityReason('');
      fetchCharger();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Gabim';
      toast.error(msg);
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleClearCache = async () => {
    setActionLoading(true);
    try {
      await clearChargerCache(id);
      toast.success('Cache u pastrua');
      fetchCharger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    setActionLoading(true);
    try {
      await generateChargerQR(id);
      toast.success('QR kodet u gjeneruan');
      fetchCharger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteCharger(id);
      toast.success('Karikuesi u fshi');
      navigate('/chargers');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateConnector = async (e) => {
    e.preventDefault();
    try {
      await createConnector({
        charger_id: id,
        connector_id: parseInt(connectorForm.connector_id),
        connector_type: connectorForm.connector_type,
        max_power_kw: parseFloat(connectorForm.max_power_kw)
      });
      toast.success('Konektori u krijua');
      setConnectorDialogOpen(false);
      setConnectorForm({ connector_id: '', connector_type: 'Type2', max_power_kw: '' });
      fetchCharger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  // Delete connector
  const handleDeleteConnector = async (connectorId) => {
    if (!window.confirm(`Jeni të sigurt që doni të fshini konektorin ${String.fromCharCode(64 + connectorId)}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connectors/${id}/${connectorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('albmove_token')}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Gabim gjatë fshirjes');
      }
      
      toast.success(`Konektori ${String.fromCharCode(64 + connectorId)} u fshi`);
      fetchCharger();
    } catch (error) {
      toast.error(error.message || 'Gabim gjatë fshirjes së konektorit');
    }
  };

  // Load clients for Remote Start selection
  const loadClientsForRemoteStart = useCallback(async (silent = false) => {
    try {
      const response = await getClientsSimple();
      setAvailableClients(response.data.clients || []);
      setAvailableAdmins(response.data.admins || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
      if (!silent) {
        toast.error('Gabim në ngarkimin e klientëve');
      }
    }
  }, []);

  useEffect(() => {
    loadClientsForRemoteStart(true);
  }, [loadClientsForRemoteStart]);

  const openSessionInSessionsPage = (session) => {
    navigate(`/sessions?search=${encodeURIComponent(session.id)}`);
  };

  const openInvoicePreview = (session, autoDownload = false) => {
    setInvoicePreviewSession(autoDownload ? { ...session, _autoDownload: true } : session);
  };

  // Handle Remote Start Transaction
  const handleRemoteStart = async () => {
    if (!charger?.charger_id || !remoteStartConnector || !remoteStartUserId) {
      toast.error('Zgjidhni një përdorues');
      return;
    }
    
    setRemoteStartLoading(true);
    try {
      const response = await remoteStartTransaction(
        charger.charger_id, 
        remoteStartConnector.connector_id,
        remoteStartUserId
      );
      
      if (response.data.success || response.data.status === 'Accepted') {
        toast.success('Karikimi u nis me sukses!', {
          description: `Porta ${String.fromCharCode(64 + remoteStartConnector.connector_id)} po fillon karikimin`
        });
        setRemoteStartDialogOpen(false);
        setRemoteStartUserId('');
        setRemoteStartConnector(null);
        // Refresh data after a short delay
        setTimeout(() => {
          fetchCharger();
        }, 2000);
      } else {
        toast.warning('Komanda u dërgua', {
          description: response.data.message || 'Duke pritur konfirmimin nga karikuesi'
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gabim në nisjen e karikimit';
      toast.error(errorMsg);
    } finally {
      setRemoteStartLoading(false);
    }
  };

  // Force Commands (Admin only)
  const handleForceReset = async (resetType = 'Soft') => {
    if (!charger?.charger_id) return;
    setActionLoading(true);
    try {
      const response = await forceChargerCommand(charger.charger_id, 'reset', resetType);
      if (response.data.success) {
        toast.success(`Reset ${resetType} u dërgua`, {
          description: response.data.message
        });
      } else {
        toast.warning('Komanda u dërgua', {
          description: response.data.message
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e komandës');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceUnlock = async (connectorId = 1) => {
    if (!charger?.charger_id) return;
    setActionLoading(true);
    try {
      const response = await forceChargerCommand(charger.charger_id, 'unlock', 'Soft', connectorId);
      toast.success('Unlock u dërgua', {
        description: response.data.message
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e komandës');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceClearCache = async () => {
    if (!charger?.charger_id) return;
    setActionLoading(true);
    try {
      const response = await forceChargerCommand(charger.charger_id, 'clear_cache');
      toast.success('Clear Cache u dërgua', {
        description: response.data.message
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e komandës');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceReconnect = async () => {
    if (!charger?.charger_id) return;
    setSyncLoading(true);
    try {
      const response = await forceReconnect(charger.charger_id);
      if (response.data.is_online) {
        toast.success('Karikuesi është Online', {
          description: response.data.message
        });
      } else {
        toast.info('Karikuesi është Offline', {
          description: response.data.message
        });
      }
      fetchCharger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në sinkronizim');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleClearStuckStatus = async () => {
    setActionLoading(true);
    try {
      const response = await resetChargerPlatformState(id);
      toast.success('Statusi u pastrua', {
        description: response.data?.message || 'Statusi i ngecur u resetua në panel'
      });
      fetchCharger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në pastrimin e statusit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFirmwareUpload = async () => {
    if (!firmwareFile) {
      toast.error('Zgjidhni skedarin e firmware');
      return;
    }

    if (!firmwareFile.name?.toLowerCase().endsWith('.tar.bz2')) {
      toast.error('Firmware duhet të jetë skedar .tar.bz2');
      return;
    }

    setFirmwareLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', firmwareFile);
      if (firmwareVersion.trim()) {
        formData.append('firmware_version', firmwareVersion.trim());
      }

      const response = await uploadChargerFirmware(id, formData);
      if (response.data.success) {
        toast.success('Komanda e firmware u dërgua', {
          description: response.data.message,
        });
      } else {
        toast.warning('Komanda u dërgua', {
          description: response.data.message,
        });
      }

      setFirmwareDialogOpen(false);
      setFirmwareFile(null);
      setFirmwareVersion('');
      fetchCharger();
      if (activeTab === 'diagnostics') {
        fetchDiagnostics();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në dërgimin e firmware');
    } finally {
      setFirmwareLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-48" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (!charger) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-12 h-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Karikuesi nuk u gjet</p>
        <Button variant="outline" onClick={() => navigate('/chargers')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Kthehu te lista
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="charger-detail-page">
      {/* WebSocket Connection Indicator */}
      {wsConnected && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400 px-3 py-1.5 rounded-full text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          Live
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/chargers')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
            charger.status === 'Faulted' ? 'bg-red-500/10' :
            charger.is_online ? 'bg-green-500/10' : 'bg-muted'
          }`}>
            {charger.status === 'Faulted' ? (
              <AlertTriangle className="w-7 h-7 text-red-500" />
            ) : charger.is_online ? (
              <Zap className="w-7 h-7 text-green-500" />
            ) : (
              <WifiOff className="w-7 h-7 text-muted-foreground" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground font-outfit">{charger.name || charger.charger_id}</h1>
              {/* Single status badge - combines online/offline with connector status */}
              {charger.is_online ? (
                <span className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/30">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                  <span className="text-sm font-medium text-green-500">Online</span>
                </span>
              ) : (
                <span className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-sm font-medium text-red-500">Offline</span>
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1">
              {charger.vendor} - {charger.model} | OCPP {charger.ocpp_version} | {charger.max_power_kw} kW
            </p>
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          {/* All action buttons inline */}
          <div className="flex items-center gap-2 flex-wrap">
            {canManageCharger ? (
              <>
                {/* Rindiz (Soft Reset) */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRestart}
                  disabled={!charger.is_online || restartLoading}
                  className={cn(
                    "transition-all",
                    charger.is_online 
                      ? "border-green-500/50 hover:bg-green-500/10 hover:border-green-500 hover:text-foreground" 
                      : "opacity-50 cursor-not-allowed"
                  )}
                  title={charger.is_online ? "Rindiz (Soft Reset)" : "Karikuesi është OFFLINE - komandat remote nuk janë të disponueshme"}
                  data-testid="cmd-restart-btn"
                >
                  {restartLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RotateCcw className="w-4 h-4 mr-2 text-green-500" />
                  )}
                  <span className="hidden sm:inline">Rindiz</span>
                </Button>

                {/* Reset (Hard Reset) */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setResetDialogOpen(true)}
                  disabled={!charger.is_online || resetLoading}
                  className={cn(
                    "transition-all",
                    charger.is_online 
                      ? "border-orange-500/50 hover:bg-orange-500/10 hover:border-orange-500 hover:text-foreground" 
                      : "opacity-50 cursor-not-allowed"
                  )}
                  title={charger.is_online ? "Reset (Hard Reset) - Ristartim i plotë" : "Karikuesi është OFFLINE - komandat remote nuk janë të disponueshme"}
                  data-testid="cmd-reset-btn"
                >
                  {resetLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCcw className="w-4 h-4 mr-2 text-orange-500" />
                  )}
                  <span className="hidden sm:inline">Reset</span>
                </Button>

                {/* Fike (Set Unavailable) */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAvailabilityDialogOpen(true)}
                  disabled={!charger.is_online || charger.status === 'Unavailable' || deactivateLoading}
                  className={cn(
                    "transition-all",
                    charger.is_online && charger.status !== 'Unavailable'
                      ? "border-red-500/50 hover:bg-red-500/10 hover:border-red-500 hover:text-foreground" 
                      : "opacity-50 cursor-not-allowed"
                  )}
                  title={
                    !charger.is_online 
                      ? "Karikuesi është OFFLINE - komandat remote nuk janë të disponueshme"
                      : charger.status === 'Unavailable'
                        ? "Karikuesi është tashmë i çaktivizuar"
                        : "Fike - Vendos jo i disponueshëm"
                  }
                  data-testid="cmd-deactivate-btn"
                >
                  {deactivateLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <StopCircle className="w-4 h-4 mr-2 text-red-500" />
                  )}
                  <span className="hidden sm:inline">Fike</span>
                </Button>

                {/* Sync Status */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleForceReconnect}
                  disabled={syncLoading}
                  className="border-blue-500/50 hover:bg-blue-500/10 hover:border-blue-500"
                  title="Sinkronizo statusin e karikuesit"
                  data-testid="cmd-sync-btn"
                >
                  {syncLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCcw className="w-4 h-4 text-blue-500" />
                  )}
                  <span className="ml-1">Sync</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearStuckStatus}
                  disabled={actionLoading}
                  className="border-amber-500/50 hover:bg-amber-500/10 hover:border-amber-500"
                  title="Pastro statusin e ngecur në panel"
                  data-testid="cmd-clear-stuck-status-btn"
                >
                  {actionLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Hammer className="w-4 h-4 text-amber-500" />
                  )}
                  <span className="ml-1">Pastro Statusin</span>
                </Button>

                {/* Aktivizo (Set Available) */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFirmwareDialogOpen(true)}
                  disabled={!charger.is_online || firmwareLoading}
                  className={cn(
                    "transition-all",
                    charger.is_online
                      ? "border-indigo-500/50 hover:bg-indigo-500/10 hover:border-indigo-500 hover:text-foreground"
                      : "opacity-50 cursor-not-allowed"
                  )}
                  title={charger.is_online ? "Ngarko dhe dërgo firmware" : "Karikuesi është OFFLINE - komandat remote nuk janë të disponueshme"}
                  data-testid="cmd-firmware-btn"
                >
                  {firmwareLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2 text-indigo-500" />
                  )}
                  <span className="hidden sm:inline">Firmware</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleActivate}
                  disabled={!charger.is_online || charger.status === 'Faulted' || charger.status === 'Available' || activateLoading}
                  className={cn(
                    "transition-all",
                    charger.is_online && charger.status !== 'Faulted' && charger.status !== 'Available'
                      ? "border-blue-500/50 hover:bg-blue-500/10 hover:border-blue-500 hover:text-foreground" 
                      : "opacity-50 cursor-not-allowed"
                  )}
                  title={
                    !charger.is_online 
                      ? "Karikuesi është OFFLINE - komandat remote nuk janë të disponueshme"
                      : charger.status === 'Faulted'
                        ? "Karikuesi ka defekt aktiv - nuk mund të aktivizohet"
                        : charger.status === 'Available'
                          ? "Karikuesi është tashmë i aktivizuar"
                          : "Aktivizo - Vendos i disponueshëm"
                  }
                  data-testid="cmd-activate-btn"
                >
                  {activateLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2 text-blue-500" />
                  )}
                  <span className="hidden sm:inline">Aktivizo</span>
                </Button>

                {/* Separator */}
                <div className="hidden sm:block w-px h-6 bg-border mx-1" />

                {/* Modifiko with pencil icon */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/chargers/${id}/edit`)} 
                  data-testid="edit-charger-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Modifiko</span>
                </Button>
              </>
            ) : (
              <div className="text-xs text-muted-foreground px-2 py-1 rounded-md bg-muted/50">
                Modalitet leximi: vetëm monitorim për moderatorin
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setExcelOpen(true)}
              data-testid="export-app-charges-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-500" />
              <span className="hidden sm:inline">Eksporto Excel</span>
            </Button>
            
            {/* Opsione të tjera dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={actionLoading} data-testid="charger-actions-btn">
                  {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleGenerateQR}>
                  <QrCode className="w-4 h-4 mr-2" />
                  Gjenero QR
                </DropdownMenuItem>
                {charger.latitude && charger.longitude && (
                  <DropdownMenuItem 
                    onClick={() => window.open(`https://www.google.com/maps?q=${charger.latitude},${charger.longitude}`, '_blank')}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Shiko në Hartë
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate(`/sessions?charger_id=${id}`)}>
                  <History className="w-4 h-4 mr-2" />
                  Shko te Seancat
                </DropdownMenuItem>
                
                {canManageCharger && charger.is_online && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleForceReset('Soft')}>
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      Soft Reset
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleForceReset('Hard')}>
                      <RotateCcw className="w-4 h-4 mr-2 text-red-400" />
                      Hard Reset
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleForceUnlock(1)}>
                      <Unlock className="w-4 h-4 mr-2" />
                      Unlock Porta A
                    </DropdownMenuItem>
                    {connectors.length > 1 && (
                      <DropdownMenuItem onClick={() => handleForceUnlock(2)}>
                        <Unlock className="w-4 h-4 mr-2" />
                        Unlock Porta B
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleForceClearCache}>
                      <Database className="w-4 h-4 mr-2" />
                      Clear Cache
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFirmwareDialogOpen(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Update Firmware
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleClearStuckStatus}>
                      <Hammer className="w-4 h-4 mr-2 text-amber-500" />
                      Pastro Statusin e Ngelur
                    </DropdownMenuItem>
                  </>
                )}
                
                {canManageCharger && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setDeleteDialogOpen(true)}
                      className="text-red-500 focus:text-red-500"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Fshi
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Offline warning */}
          {!charger.is_online && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
              <WifiOff className="w-3 h-3" />
              <span>Komandat remote nuk janë të disponueshme kur karikuesi është offline</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 h-auto gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Info className="w-4 h-4 mr-2" />
            Përmbledhje
          </TabsTrigger>
          <TabsTrigger value="energy" data-testid="tab-energy">
            <BarChart3 className="w-4 h-4 mr-2" />
            Energji
          </TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">
            <History className="w-4 h-4 mr-2" />
            Seanca
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <Copy className="w-4 h-4 mr-2" />
            Faturat
          </TabsTrigger>
          <TabsTrigger value="errors" data-testid="tab-errors">
            <AlertCircle className="w-4 h-4 mr-2" />
            Gabime
          </TabsTrigger>
          <TabsTrigger value="diagnostics" data-testid="tab-diagnostics">
            <Terminal className="w-4 h-4 mr-2" />
            Diagnostikë
          </TabsTrigger>
        </TabsList>

        {/* KPI Cards with Date Range Selector */}
        <div className="space-y-4">
          {/* Date Range Selector - Larger with Custom option */}
          <div className="flex items-center justify-end gap-2">
            <Select 
              value={dateRange} 
              onValueChange={(v) => {
                if (v === 'custom') {
                  setCustomDateDialogOpen(true);
                } else {
                  setDateRange(v);
                }
              }}
            >
              <SelectTrigger className="w-[180px] h-10">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue>
                  {dateRange === 'custom' && customDateFrom && customDateTo 
                    ? `${customDateFrom.split('-').reverse().slice(0,2).join('/')} - ${customDateTo.split('-').reverse().slice(0,2).join('/')}`
                    : dateRange === '24h' ? '24 orë'
                    : dateRange === '7d' ? '1 javë'
                    : dateRange === '30d' ? '1 muaj'
                    : dateRange === '90d' ? '3 muaj'
                    : 'Zgjidh periudhën'
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 orë</SelectItem>
                <SelectItem value="7d">1 javë</SelectItem>
                <SelectItem value="30d">1 muaj</SelectItem>
                <SelectItem value="90d">3 muaj</SelectItem>
                <SelectItem value="custom">📅 Periudhë e personalizuar...</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* KPI Cards Grid - 5 Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Battery className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{kpis?.total_kwh || 0}</p>
                    <p className="text-xs text-muted-foreground">kWh totale</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-500 font-bold text-lg">L</span>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{(kpis?.total_revenue || 0).toLocaleString('sq-AL')}</p>
                    <p className="text-xs text-muted-foreground">Të Ardhura (Kredite)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <History className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{kpis?.total_sessions || 0}</p>
                    <p className="text-xs text-muted-foreground">Seanca</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-500/10 to-transparent border-yellow-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{kpis?.avg_session_kwh || 0}</p>
                    <p className="text-xs text-muted-foreground">kWh mesatare</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{kpis?.uptime_percent || 0}%</p>
                    <p className="text-xs text-muted-foreground">Uptime</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Overview Tab - REDESIGNED */}
        <TabsContent value="overview" className="space-y-6">
          {/* Row 1: 3 Cards - Info, Location, Connectors */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Card 1: Informacione + OCPP */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Informacione
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div className="text-muted-foreground">Vendor</div>
                  <div className="font-medium">{charger.vendor || '-'}</div>
                  <div className="text-muted-foreground">Model</div>
                  <div className="font-medium">{charger.model || '-'}</div>
                  <div className="text-muted-foreground">Serial</div>
                  <div className="font-mono text-xs">{charger.serial_number || '-'}</div>
                  <div className="text-muted-foreground">Firmware</div>
                  <div className="font-mono text-xs">{charger.firmware_version || '-'}</div>
                  <div className="text-muted-foreground">OCPP</div>
                  <div className="font-medium">{charger.ocpp_version || '1.6J'}</div>
                  <div className="text-muted-foreground">Fuqia Max</div>
                  <div className="font-medium">{charger.max_power_kw || 0} kW</div>
                </div>
                
                {/* OCPP URL inside this card */}
                <div className="pt-3 border-t border-border">
                  <p className="text-xs font-medium text-primary mb-2 flex items-center gap-1">
                    <Link className="w-3 h-3" />
                    OCPP Konfigurim
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[10px] bg-muted px-2 py-1.5 rounded font-mono text-green-500 break-all">
                      wss://ocpp.albmove.al/1.6/{charger.charger_id}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => {
                        navigator.clipboard.writeText(`wss://ocpp.albmove.al/1.6/${charger.charger_id}`);
                        toast.success('URL u kopjua!');
                      }}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Vendndodhja */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Vendndodhja
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="space-y-2">
                  <div>
                    <p className="text-muted-foreground text-xs">Emri/Site</p>
                    <p className="font-medium">{charger.site?.name || charger.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Adresa</p>
                    <p className="font-medium">{charger.address || charger.site?.address || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Qyteti</p>
                    <p className="font-medium">{charger.city || charger.site?.city || '-'}</p>
                  </div>
                  {charger.latitude && charger.longitude && (
                    <div>
                      <p className="text-muted-foreground text-xs">Koordinatat</p>
                      <p className="font-mono text-xs">{charger.latitude.toFixed(5)}, {charger.longitude.toFixed(5)}</p>
                    </div>
                  )}
                </div>
                
                {/* Google Maps Button */}
                {charger.latitude && charger.longitude && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => window.open(`https://www.google.com/maps?q=${charger.latitude},${charger.longitude}`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Hap në Google Maps
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Card 3: Konektorët */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-outfit flex items-center gap-2">
                    <Plug className="w-4 h-4" />
                    Konektorët ({connectors.length})
                  </CardTitle>
                  {canManageCharger && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConnectorDialogOpen(true)}
                      className="h-7 text-xs"
                      data-testid="add-connector-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Shto
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {connectors.length === 0 ? (
                  <div className="text-center py-6">
                    <Plug className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">Nuk ka konektorë</p>
                    {canManageCharger && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConnectorDialogOpen(true)}
                        className="mt-3"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Shto Konektor
                      </Button>
                    )}
                  </div>
                ) : (
                  connectors.map((conn) => {
                    const letter = conn.label || String.fromCharCode(64 + conn.connector_id);
                    const connType = conn.connector_type || conn.type || 'Unknown';
                    // Use connector's own power - source of truth. No charger-level fallback
                    const powerKw = conn.max_power_kw ?? null;
                    
                    // Check if charger is offline - all ports become unavailable
                    const isChargerOffline = !charger.is_online;
                    const isAvailable = !isChargerOffline && conn.status === 'Available';
                    const isCharging = !isChargerOffline && conn.status === 'Charging';
                    const isPreparing = !isChargerOffline && conn.status === 'Preparing';
                    const isFinishing = !isChargerOffline && conn.status === 'Finishing';
                    const isFaulted = conn.status === 'Faulted';
                    
                    return (
                      <div 
                        key={conn.connector_id}
                        className={`flex items-center gap-4 p-3 rounded-lg border transition-all ${
                          isChargerOffline ? 'border-zinc-700 bg-zinc-800/30' :
                          isCharging ? 'border-blue-500 bg-blue-500/5' :
                          isAvailable ? 'border-green-500/50 bg-green-500/5' :
                          isPreparing ? 'border-yellow-500/50 bg-yellow-500/5' :
                          'border-border bg-muted/30'
                        }`}
                      >
                        {/* Inline: Icon + Letter + Type + Power */}
                        <img 
                          src={`/images/connectors/${connType?.toLowerCase()?.replace(/\s/g, '_')}.png`}
                          alt={connType}
                          className="w-8 h-8 object-contain"
                          onError={(e) => { e.target.src = ''; e.target.className = 'hidden'; }}
                        />
                        
                        <span className="font-bold text-xl min-w-[24px]">{letter}</span>
                        
                        <span className="text-sm text-muted-foreground">{connType}</span>
                        
                        <div className="flex items-center gap-1 text-sm">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{powerKw != null ? `${powerKw} kW` : 'N/A'}</span>
                        </div>
                        
                        <div className="flex-1" />
                        
                        {/* Status Badge with animations */}
                        {isChargerOffline ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-700/50 text-zinc-400">
                            <span className="w-2 h-2 rounded-full bg-zinc-500" />
                            <span className="text-xs font-medium">Jo i disponueshëm</span>
                          </div>
                        ) : isAvailable ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 text-green-400">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            <span className="text-xs font-medium">I Lirë</span>
                          </div>
                        ) : isCharging ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400">
                            <Zap className="w-3 h-3 text-blue-400" />
                            <span className="text-xs font-medium">Po Karikon</span>
                          </div>
                        ) : isPreparing ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-400">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            <span className="text-xs font-medium">Përgatitet</span>
                          </div>
                        ) : isFinishing ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/20 text-purple-400">
                            <CheckCircle className="w-3 h-3" />
                            <span className="text-xs font-medium">Po Mbaron</span>
                          </div>
                        ) : isFaulted ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 text-red-400">
                            <AlertTriangle className="w-3 h-3 animate-pulse" />
                            <span className="text-xs font-medium">Defekt</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-500/20 text-zinc-400">
                            <span className="w-2 h-2 rounded-full bg-zinc-500" />
                            <span className="text-xs font-medium">{conn.status || 'N/A'}</span>
                          </div>
                        )}
                        
                        {/* QR Code Button */}
                        <button
                          onClick={() => {
                            setSelectedConnector({
                              ...conn,
                              qr_code: `albmove://c/${charger?.charger_id || charger?.id}/${conn.connector_id}`
                            });
                            setQrDialogOpen(true);
                          }}
                          className="ml-2 p-1.5 rounded-lg text-zinc-400 hover:text-primary hover:bg-primary/10 transition-colors"
                          title="Shfaq QR Code"
                          data-testid={`qr-connector-${conn.connector_id}`}
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        
                        {/* Delete Button */}
                        {canManageCharger && (
                          <button
                            onClick={() => handleDeleteConnector(conn.connector_id)}
                            className="ml-2 p-1.5 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Fshi konektorin"
                            data-testid={`delete-connector-${conn.connector_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Energy Chart + Sessions Table - 50% each */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Card: Energjia e Konsumuar */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <Battery className="w-4 h-4" />
                  Trendi i Energjisë
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Mini Energy Chart */}
                {energyLoading ? (
                  <div className="h-[150px] flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : energyData?.length > 0 ? (
                  <div className="h-[150px]">
                    <ResponsiveContainer width="100%" height={150}>
                      <AreaChart data={energyData}>
                        <defs>
                          <linearGradient id="energyGradientOverview" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" tick={{fontSize: 10}} stroke="#6b7280" tickFormatter={(v) => v?.slice(5) || ''} />
                        <YAxis tick={{fontSize: 10}} stroke="#6b7280" width={30} />
                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', fontSize: 12 }} />
                        <Area type="monotone" dataKey="energy_kwh" stroke="#10b981" fill="url(#energyGradientOverview)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[150px] flex items-center justify-center text-sm text-muted-foreground">
                    Nuk ka të dhëna për periudhën e zgjedhur
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={() => setActiveTab('energy')}
                >
                  Shiko Raportin e Plotë
                </Button>
              </CardContent>
            </Card>

            {/* Card: Seancat e Fundit */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-outfit flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Seancat e Karikimit
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setActiveTab('sessions')}
                  >
                    Shiko të gjitha
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sessionsLoading ? (
                  <div className="h-[250px] flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : sessions?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="w-[100px]">Klienti</TableHead>
                          <TableHead className="w-[40px]">Port</TableHead>
                          <TableHead className="w-[70px]">Fillimi</TableHead>
                          <TableHead className="w-[50px]">kWh</TableHead>
                          <TableHead className="w-[60px]">Kredite</TableHead>
                          <TableHead className="w-[60px]">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sessions.slice(0, 5).map((session) => {
                          const normalizedSession = normalizeChargingInvoice(session);
                          return (
                          <TableRow key={session.id} className="text-xs">
                            <TableCell className="font-medium truncate max-w-[100px]">
                              {session.user_name || session.user_email?.split('@')[0] || '-'}
                            </TableCell>
                            <TableCell>{String.fromCharCode(64 + (session.connector_id || 1))}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {session.start_time ? new Date(session.start_time).toLocaleDateString('sq-AL', {day: '2-digit', month: '2-digit'}) : '-'}
                            </TableCell>
                            <TableCell>{(normalizedSession?.energy_kwh || 0).toFixed(2)}</TableCell>
                            <TableCell>{(normalizedSession?.cost_lek || 0).toFixed(2)}</TableCell>
                            <TableCell>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                session.status === 'completed' || session.status === 'Completed' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : session.status === 'Charging'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {session.status === 'completed' || session.status === 'Completed' ? 'OK' : 
                                 session.status === 'Charging' ? 'Live' : session.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        );})}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-sm text-muted-foreground">
                    Nuk ka seanca për këtë karikues
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Energy Tab */}
        <TabsContent value="energy" className="space-y-6">
          {/* KPIs */}
          {kpis && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Battery className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{kpis.total_kwh}</p>
                      <p className="text-xs text-muted-foreground">kWh totale</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <History className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{kpis.total_sessions}</p>
                      <p className="text-xs text-muted-foreground">Seanca</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{kpis.avg_session_kwh}</p>
                      <p className="text-xs text-muted-foreground">kWh mesatare</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{kpis.fault_count}</p>
                      <p className="text-xs text-muted-foreground">Defekte</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Chart Controls */}
          <div className="flex items-center gap-4">
            <Select value={energyGroupBy} onValueChange={setEnergyGroupBy}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hour">Ora</SelectItem>
                <SelectItem value="day">Ditë</SelectItem>
                <SelectItem value="week">Javë</SelectItem>
                <SelectItem value="month">Muaj</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchEnergy}>
              <RefreshCw className={cn("w-4 h-4", energyLoading && "animate-spin")} />
            </Button>
          </div>

          {/* Energy Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Konsumi i Energjisë</CardTitle>
              <CardDescription>kWh sipas periudhës</CardDescription>
            </CardHeader>
            <CardContent>
              {energyLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : energyData.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Nuk ka të dhëna për këtë periudhë
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={energyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="period" stroke="var(--muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'var(--card)', 
                        border: '1px solid var(--border)',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="kwh" name="kWh" fill="#1d62a7" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="sessions" name="Seanca" fill="#afd039" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Seanca të Karikimit</CardTitle>
              <CardDescription>
                {sessionsTotal} seanca gjithsej
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-[1.2fr_220px_260px_auto]">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={sessionSearchQuery}
                    onChange={(e) => setSessionSearchQuery(e.target.value)}
                    placeholder="Kërko sipas seancës, faturës, targës..."
                    className="pl-9"
                  />
                </div>
                <Select value={sessionStatusFilter} onValueChange={setSessionStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Statusi" />
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sessionUserFilter} onValueChange={setSessionUserFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Përdoruesi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjithë përdoruesit</SelectItem>
                    {availableAdmins.map((admin) => (
                      <SelectItem key={`admin-${admin.id}`} value={admin.id}>
                        {`Admin: ${admin.full_name || admin.email}`}
                      </SelectItem>
                    ))}
                    {availableClients.map((client) => (
                      <SelectItem key={`client-${client.id}`} value={client.id}>
                        {client.full_name || client.email || client.phone || client.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 lg:justify-end">
                  <Button variant="outline" size="sm" onClick={() => {
                    setSessionSearchQuery('');
                    setSessionStatusFilter('all');
                    setSessionUserFilter('all');
                  }}>
                    Pastro
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchSessions}>
                    <RefreshCw className={cn('w-4 h-4 mr-2', sessionsLoading && 'animate-spin')} />
                    Rifresko
                  </Button>
                </div>
              </div>

              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <span>Periudha aktive vjen nga selektori i datës sipër.</span>
                <span>Detajet e seancës dhe fatura hapen direkt nga kjo tabelë.</span>
              </div>

              {sessionsLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nuk ka seanca për këtë periudhë</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Sesioni</TableHead>
                        <TableHead>Përdoruesi</TableHead>
                        <TableHead>Konektor</TableHead>
                        <TableHead>Fillimi</TableHead>
                        <TableHead>Mbarimi</TableHead>
                        <TableHead>Energji</TableHead>
                        <TableHead>Kosto</TableHead>
                        <TableHead>Kohëzgjatja</TableHead>
                        <TableHead>Fatura</TableHead>
                        <TableHead>Statusi</TableHead>
                        <TableHead className="text-right">Veprime</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map((session) => {
                        const normalizedSession = normalizeChargingInvoice(session);
                        return (
                        <TableRow
                          key={session.id}
                          className="border-border cursor-pointer hover:bg-muted/30"
                          onClick={() => setDetailsSession(session)}
                        >
                          <TableCell>
                            <div>
                              <button
                                type="button"
                                className="font-mono text-xs text-primary underline-offset-4 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDetailsSession(session);
                                }}
                              >
                                {String(session.id).slice(0, 8).toUpperCase()}
                              </button>
                              <p className="text-xs text-muted-foreground">{session.license_plate || session.vehicle_plate || 'Pa targë'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <p>{session.user_name || '-'}</p>
                                <p className="text-xs text-muted-foreground">{session.user_email || session.user_email_masked || '-'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{String.fromCharCode(64 + (session.connector_id || 1))}</p>
                              <p className="text-xs text-muted-foreground">{session.connector_type || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(session.start_time)}</TableCell>
                          <TableCell>{session.end_time ? formatDate(session.end_time) : 'Në vazhdim'}</TableCell>
                          <TableCell>{formatEnergy(normalizedSession?.energy_kwh || 0)}</TableCell>
                          <TableCell>{formatCurrency(normalizedSession?.cost_lek || 0, 'Kredite')}</TableCell>
                          <TableCell>{formatDuration(normalizedSession?.duration_minutes)}</TableCell>
                          <TableCell>
                            {isBillableSession(session) ? (
                              <button
                                type="button"
                                className="text-xs text-primary underline-offset-4 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInvoicePreview(session);
                                }}
                              >
                                {getInvoiceNumber(session)}
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Pa faturë</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={session.status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDetailsSession(session);
                                }}
                                title="Detajet"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInvoicePreview(session);
                                }}
                                title="Fatura"
                                disabled={!isBillableSession(session)}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSessionInSessionsPage(session);
                                }}
                                title="Hap te faqja e seancave"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Faqja {sessionsPage} nga {Math.ceil(sessionsTotal / 10)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={sessionsPage === 1}
                        onClick={() => setSessionsPage(p => p - 1)}
                      >
                        Para
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={sessionsPage >= Math.ceil(sessionsTotal / 10)}
                        onClick={() => setSessionsPage(p => p + 1)}
                      >
                        Pas
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-500" />
                Faturat e Karikuesit
              </CardTitle>
              <CardDescription>
                Të gjitha faturat e krijuara nga seancat e këtij karikuesi në periudhën e zgjedhur.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <span>Filtrat e përdoruesit dhe kërkimit përdoren edhe për faturat.</span>
                <span>Hap preview të faturës ose seancën përkatëse me një klik.</span>
              </div>

              {invoicesLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : invoiceSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nuk ka fatura për filtrat aktualë</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Nr. Faturës</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Përdoruesi</TableHead>
                        <TableHead>Seanca</TableHead>
                        <TableHead>Energji</TableHead>
                        <TableHead>Totali</TableHead>
                        <TableHead>Statusi</TableHead>
                        <TableHead className="text-right">Veprime</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoiceSessions.map((session) => {
                        const normalizedSession = normalizeChargingInvoice(session);
                        return (
                        <TableRow key={session.id} className="border-border hover:bg-muted/30">
                          <TableCell>
                            <button
                              type="button"
                              className="font-mono text-xs text-primary underline-offset-4 hover:underline"
                              onClick={() => openInvoicePreview(session)}
                            >
                              {getInvoiceNumber(session)}
                            </button>
                          </TableCell>
                          <TableCell>{formatDate(session.end_time || session.start_time)}</TableCell>
                          <TableCell>
                            <div>
                              <p>{session.user_name || '-'}</p>
                              <p className="text-xs text-muted-foreground">{session.user_email || session.user_email_masked || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              className="font-mono text-xs text-primary underline-offset-4 hover:underline"
                              onClick={() => setDetailsSession(session)}
                            >
                              {String(session.id).slice(0, 8).toUpperCase()}
                            </button>
                          </TableCell>
                          <TableCell>{formatEnergy(normalizedSession?.energy_kwh || 0)}</TableCell>
                          <TableCell>{formatCurrency(normalizedSession?.cost_lek || 0, 'Kredite')}</TableCell>
                          <TableCell>
                            <StatusBadge status={session.status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openInvoicePreview(session)} title="Shiko faturën">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openInvoicePreview(session, true)} title="Shkarko faturën">
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openSessionInSessionsPage(session)} title="Hap te seancat">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Faqja {invoicePage} nga {Math.max(1, Math.ceil(invoiceTotal / 10))}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={invoicePage === 1}
                        onClick={() => setInvoicePage((page) => page - 1)}
                      >
                        Para
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={invoicePage >= Math.max(1, Math.ceil(invoiceTotal / 10))}
                        onClick={() => setInvoicePage((page) => page + 1)}
                      >
                        Pas
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Gabime dhe Defekte
              </CardTitle>
              <CardDescription>
                {errorsTotal} gabime gjithsej
              </CardDescription>
            </CardHeader>
            <CardContent>
              {errorsLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : errors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nuk ka gabime të regjistruara për këtë periudhë</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Data</TableHead>
                        <TableHead>Lloji</TableHead>
                        <TableHead>Mesazhi</TableHead>
                        <TableHead>Detaje</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {errors.map((error, idx) => (
                        <TableRow key={error.id || idx} className="border-border">
                          <TableCell>{formatDate(error.timestamp)}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs bg-red-500/10 text-red-500">
                              {error.event_type || error.action || 'Error'}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {error.message || error.details?.message || '-'}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-muted-foreground">
                            {JSON.stringify(error.details || {})}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Faqja {errorsPage} nga {Math.ceil(errorsTotal / 10)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={errorsPage === 1}
                        onClick={() => setErrorsPage(p => p - 1)}
                      >
                        Para
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={errorsPage >= Math.ceil(errorsTotal / 10)}
                        onClick={() => setErrorsPage(p => p + 1)}
                      >
                        Pas
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Diagnostics Tab */}
        <TabsContent value="diagnostics" className="space-y-6">
          {diagnosticsLoading ? (
            <div className="h-[200px] flex items-center justify-center">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : diagnostics ? (
            <div className="space-y-6">
              {/* Connector Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Plug className="w-5 h-5" />
                    Statistika të Konektorëve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {diagnostics.connector_stats?.map((conn) => (
                      <div key={conn.connector_id} className="p-4 rounded-lg bg-muted/50 border border-border">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium">Konektor {String.fromCharCode(64 + conn.connector_id)}</span>
                          <StatusBadge status={conn.status} />
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Lloji</span>
                            <span>{conn.connector_type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Fuqia Max</span>
                            <span>{conn.max_power_kw} kW</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Seanca totale</span>
                            <span className="text-primary">{conn.session_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Gabime</span>
                            <span className={conn.fault_count > 0 ? 'text-red-500' : 'text-green-500'}>
                              {conn.fault_count}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Command History */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Terminal className="w-5 h-5" />
                    Historiku i Komandave
                  </CardTitle>
                  <CardDescription>Komandat e fundit të ekzekutuara</CardDescription>
                </CardHeader>
                <CardContent>
                  {diagnostics.command_history?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Terminal className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Nuk ka komanda të regjistruara</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead>Data</TableHead>
                          <TableHead>Komanda</TableHead>
                          <TableHead>Ekzekutuesi</TableHead>
                          <TableHead>Detaje</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {diagnostics.command_history?.slice(0, 20).map((cmd, idx) => (
                          <TableRow key={idx} className="border-border">
                            <TableCell className="text-sm">{formatDate(cmd.timestamp)}</TableCell>
                            <TableCell>
                              <span className="px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                                {cmd.action?.replace(/_/g, ' ')}
                              </span>
                            </TableCell>
                            <TableCell>{cmd.actor_role || '-'}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-muted-foreground text-xs">
                              {JSON.stringify(cmd.details || {})}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Event Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Timeline Ngjarjesh
                  </CardTitle>
                  <CardDescription>
                    {diagnostics.stats?.total_events_30d || 0} ngjarje në 30 ditët e fundit
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {diagnostics.event_timeline?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Nuk ka ngjarje të regjistruara</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {diagnostics.event_timeline?.slice(0, 50).map((event, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className={cn(
                            "w-2 h-2 rounded-full mt-2 flex-shrink-0",
                            event.event_type === 'StatusNotification' ? 'bg-blue-500' :
                            event.event_type === 'fault' ? 'bg-red-500' :
                            event.event_type === 'BootNotification' ? 'bg-green-500' :
                            'bg-muted-foreground'
                          )} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{event.event_type}</span>
                              {event.connector_id && (
                                <span className="text-xs text-muted-foreground">
                                  Konektor {String.fromCharCode(64 + event.connector_id)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {formatDate(event.timestamp)}
                            </p>
                            {event.status && (
                              <span className="text-xs mt-1 inline-block px-2 py-0.5 rounded bg-muted">
                                {event.status}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* System Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Informacione Sistemi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Firmware</p>
                      <p className="font-medium">{diagnostics.firmware_version || '-'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">OCPP Version</p>
                      <p className="font-medium">{diagnostics.ocpp_version || '-'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Konektorë</p>
                      <p className="font-medium">{diagnostics.stats?.connector_count || 0}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Heartbeat i Fundit</p>
                      <p className="font-medium text-sm">
                        {diagnostics.last_heartbeat ? formatDate(diagnostics.last_heartbeat) : '-'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Terminal className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Nuk ka të dhëna diagnostike</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Availability Dialog (Fike/Deactivate) */}
      <Dialog open={availabilityDialogOpen} onOpenChange={setAvailabilityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <StopCircle className="w-5 h-5 text-red-500" />
              Fike Karikuesin
            </DialogTitle>
            <DialogDescription>
              Karikuesi nuk do të pranojë seanca të reja. Përdoruesit nuk do të mund të fillojnë karikim në këtë pajisje.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Arsyeja (opsionale)</Label>
              <Input
                value={availabilityReason}
                onChange={(e) => setAvailabilityReason(e.target.value)}
                placeholder="P.sh. Mirëmbajtje, defekt teknik..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeactivate}
              disabled={deactivateLoading}
            >
              {deactivateLoading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Fike
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset (Hard) Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-orange-500">
              <RefreshCcw className="w-5 h-5" />
              Reset (Hard) i Karikuesit
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">Kujdes:</span> Reset do të rindizë plotësisht karikuesin. 
              Kjo komandë duhet të përdoret kur pajisja është e bllokuar ose sillet gabim. 
              Çdo seancë aktive do të ndërpritet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <p className="text-sm text-orange-500">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                A jeni të sigurt që dëshironi të bëni Hard Reset të karikuesit?
              </p>
            </div>
            <div className="space-y-2">
              <Label>Arsyeja (opsionale)</Label>
              <Input
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="P.sh. Pajisja e bllokuar, gabim komunikimi..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleReset}
              disabled={resetLoading}
            >
              {resetLoading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Konfirmo Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={firmwareDialogOpen} onOpenChange={setFirmwareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-indigo-500">
              <Upload className="w-5 h-5" />
              Update Firmware
            </DialogTitle>
            <DialogDescription>
              Ngarko file e firmware-it dhe dërgo komandën OCPP te karikuesi
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Versioni i firmware (opsional)</Label>
              <Input
                value={firmwareVersion}
                onChange={(e) => setFirmwareVersion(e.target.value)}
                placeholder="P.sh. OCPP_R3.29-C3.08-C3.08-P3.04"
              />
            </div>
            <div className="space-y-2">
              <Label>Skedari i firmware</Label>
              <Input
                type="file"
                accept=".tar.bz2,application/x-bzip2,application/octet-stream"
                onChange={(e) => setFirmwareFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-muted-foreground">
                Formati i lejuar: .tar.bz2. Firmware aktual: {charger?.firmware_version || '-'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setFirmwareDialogOpen(false);
                setFirmwareFile(null);
                setFirmwareVersion('');
              }}
            >
              Anulo
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleFirmwareUpload}
              disabled={firmwareLoading || !charger?.is_online}
            >
              {firmwareLoading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Dërgo Firmware
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-red-500">Fshi Karikuesin</DialogTitle>
            <DialogDescription>
              Ky veprim nuk mund të zhbëhet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Anulo
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              Fshi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Connector Dialog */}
      <Dialog open={connectorDialogOpen} onOpenChange={setConnectorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Shto Konektor</DialogTitle>
            <DialogDescription>Plotësoni të dhënat e konektorit</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateConnector} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Konektor</Label>
                <Select 
                  value={connectorForm.connector_id?.toString()} 
                  onValueChange={(v) => setConnectorForm({ ...connectorForm, connector_id: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Zgjidh" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map(num => {
                      const letter = String.fromCharCode(64 + num);
                      const isUsed = connectors.some(c => c.connector_id === num);
                      return (
                        <SelectItem 
                          key={num} 
                          value={num.toString()}
                          disabled={isUsed}
                        >
                          {letter} {isUsed ? '(përdorur)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lloji</Label>
                <Select 
                  value={connectorForm.connector_type} 
                  onValueChange={(v) => setConnectorForm({ ...connectorForm, connector_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {connectorTypes.map(ct => (
                      <SelectItem key={ct.code || ct.id} value={ct.code}>
                        {ct.name} {ct.current_type ? `(${ct.current_type})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fuqia Max (kW)</Label>
              <Input
                type="number"
                step="0.1"
                value={connectorForm.max_power_kw}
                onChange={(e) => setConnectorForm({ ...connectorForm, max_power_kw: e.target.value })}
                placeholder="22"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConnectorDialogOpen(false)}>
                Anulo
              </Button>
              <Button type="submit" disabled={!connectorForm.connector_id}>Krijo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              QR Code - Porta {selectedConnector ? String.fromCharCode(64 + selectedConnector.connector_id) : ''}
            </DialogTitle>
            <DialogDescription>
              Skanoni këtë kod me aplikacionin AlbMove për të nisur karikimin
            </DialogDescription>
          </DialogHeader>
          {selectedConnector && (
            <div className="flex flex-col items-center py-6 space-y-4">
              {/* QR Code */}
              <div className="p-4 bg-white rounded-xl shadow-lg">
                <QRCodeSVG 
                  value={selectedConnector.qr_code}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              {/* Connector Info */}
              <div className="w-full bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Karikuesi</span>
                  <span className="font-mono">{charger?.name || charger?.charger_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Porta</span>
                  <span className="font-medium">{String.fromCharCode(64 + selectedConnector.connector_id)} ({selectedConnector.connector_type || 'Type 2'})</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Fuqia</span>
                  <span className="font-medium">{selectedConnector.max_power_kw || charger?.max_power_kw || 22} kW</span>
                </div>
              </div>
              
              {/* QR Data */}
              <div className="w-full">
                <p className="text-xs text-muted-foreground mb-2">URL për skanim:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted px-3 py-2 rounded font-mono break-all">
                    {selectedConnector.qr_code}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedConnector.qr_code);
                      toast.success('URL u kopjua!');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Download Button */}
              <Button 
                variant="outline" 
                size="sm"
                className="w-full"
                onClick={() => {
                  const canvas = document.querySelector('#qr-dialog-content svg');
                  if (canvas) {
                    const svgData = new XMLSerializer().serializeToString(canvas);
                    const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                    const svgUrl = URL.createObjectURL(svgBlob);
                    const downloadLink = document.createElement('a');
                    downloadLink.href = svgUrl;
                    downloadLink.download = `qr-${charger?.charger_id}-porta-${String.fromCharCode(64 + selectedConnector.connector_id)}.svg`;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                    toast.success('QR Code u shkarkua!');
                  }
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Shkarko QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remote Start Transaction Dialog */}
      <Dialog open={remoteStartDialogOpen} onOpenChange={setRemoteStartDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-500" />
              Nis Karikimin Manualisht
            </DialogTitle>
            <DialogDescription>
              Dërgoni komandën RemoteStartTransaction për të nisur karikimin në portën e zgjedhur
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Charger Info */}
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Karikuesi</span>
                <span className="font-mono text-sm">{charger?.charger_id}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-muted-foreground">Porta</span>
                <span className="font-medium">
                  #{remoteStartConnector?.connector_id} ({remoteStartConnector?.connector_type})
                </span>
              </div>
            </div>
            
            {/* User Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Zgjidhni Përdoruesin *</label>
              <select
                value={remoteStartUserId}
                onChange={(e) => setRemoteStartUserId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">-- Zgjidhni përdoruesin --</option>
                
                {availableAdmins.length > 0 && (
                  <optgroup label="Administratorë">
                    {availableAdmins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.full_name} ({admin.email}) - {admin.role}
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {availableClients.length > 0 && (
                  <optgroup label="Klientë">
                    {availableClients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.full_name} ({client.email || client.phone || 'N/A'})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <p className="text-xs text-muted-foreground">
                Karikimi do të faturohet në llogarinë e përdoruesit të zgjedhur
              </p>
            </div>
            
            {/* Warning */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-600">Kujdes</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Sigurohuni që automjeti është i lidhur fizikisht me karikuesin para se të nisni karikimin.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoteStartDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              onClick={handleRemoteStart}
              disabled={remoteStartLoading || !remoteStartUserId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {remoteStartLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Duke nisur...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Nis Karikimin
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Date Range Dialog */}
      <Dialog open={customDateDialogOpen} onOpenChange={setCustomDateDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Zgjidh Periudhën
            </DialogTitle>
            <DialogDescription>
              Vendos datën e fillimit dhe mbarimit për filtrim
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="date-from">Nga data</Label>
              <Input
                id="date-from"
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                max={customDateTo || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date-to">Deri në datë</Label>
              <Input
                id="date-to"
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                min={customDateFrom}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            {/* Quick Presets */}
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Periudha të shpejta:</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth(), 1);
                    setCustomDateFrom(start.toISOString().split('T')[0]);
                    setCustomDateTo(now.toISOString().split('T')[0]);
                  }}
                >
                  Këtë muaj
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    const end = new Date(now.getFullYear(), now.getMonth(), 0);
                    setCustomDateFrom(start.toISOString().split('T')[0]);
                    setCustomDateTo(end.toISOString().split('T')[0]);
                  }}
                >
                  Muajin e kaluar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), 0, 1);
                    setCustomDateFrom(start.toISOString().split('T')[0]);
                    setCustomDateTo(now.toISOString().split('T')[0]);
                  }}
                >
                  Këtë vit
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDateDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              onClick={() => {
                if (customDateFrom && customDateTo) {
                  setDateRange('custom');
                  setCustomDateDialogOpen(false);
                  // Trigger data refresh
                  fetchEnergy();
                } else {
                  toast.error('Zgjidhni të dy datat');
                }
              }}
              disabled={!customDateFrom || !customDateTo}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Apliko
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SessionDetailsModal
        session={detailsSession}
        onClose={() => setDetailsSession(null)}
        onRemoteStop={() => setDetailsSession(null)}
      />

      <InvoicePopup
        session={invoicePreviewSession}
        onClose={() => setInvoicePreviewSession(null)}
      />

      <AppChargesExcelDialog
        open={excelOpen}
        onOpenChange={setExcelOpen}
        scope="charger"
        targetId={id}
        targetName={charger?.name || charger?.charger_id}
      />
    </div>
  );
};

export default ChargerDetail;
